import type { ReactNode } from "react";
import { FileText } from "lucide-react";
import { useMasterProductEvidence } from "@/lib/api/clinicianHooks";
import type {
  ClinicalIntakeEvidence,
  ClinicalIntakeSubmission,
} from "@/types/intakeEvidence";
import { IntakeEvidenceDetails } from "./IntakeEvidenceDetails";
import { IntakeHighlights } from "./IntakeHighlights";

/** Keep the assessment tied to its own annual revision, including older ones. */
export function selectIntakeEvidence(
  evidence: ClinicalIntakeEvidence,
  productId?: string | null,
  orderId?: string,
) {
  const requirements = evidence.requirements.filter((item) =>
    orderId
      ? item.order_id === orderId
      : !productId || item.product_id === productId,
  );
  // An unrelated purchase of the same product is not evidence for this order.
  if (orderId && !requirements.length) return { requirements, submissions: [] };
  const relevantIds = new Set(
    requirements
      .flatMap((item) => [
        item.master_submission_id,
        item.product_submission_id,
      ])
      .filter(Boolean),
  );
  const matchingProducts = evidence.submissions.filter(
    (item) =>
      item.kind !== "master" &&
      (!productId || item.product_ids.includes(productId)),
  );
  const products = requirements.length
    ? matchingProducts.filter((item) => relevantIds.has(item.id))
    : productId
      ? matchingProducts.slice(0, 1)
      : matchingProducts;
  for (const item of products)
    if (item.master_submission_id) relevantIds.add(item.master_submission_id);
  const submissions = evidence.submissions.filter(
    (item) => relevantIds.has(item.id) || products.includes(item),
  );
  if (!submissions.length && evidence.master && !productId && !orderId)
    submissions.push(evidence.master);
  submissions.sort(
    (left, right) =>
      Number(right.kind === "master") - Number(left.kind === "master"),
  );
  return { requirements, submissions };
}

function combinedHighlights(submissions: ClinicalIntakeSubmission[]) {
  return submissions
    .flatMap((submission) =>
      (submission.highlights ?? []).map((entry) => ({
        ...entry,
        submitted_at: entry.submitted_at || submission.created_at,
        form_name:
          submission.kind === "master"
            ? "Annual intake"
            : submission.definition.title,
      })),
    )
    .sort(
      (left, right) =>
        Number(right.importance === "safety") -
        Number(left.importance === "safety"),
    );
}

/** New-model evidence replaces the legacy product preview, never duplicates it. */
export function MasterProductEvidence({
  patientId,
  productId,
  orderId,
  legacyFallback,
}: {
  patientId?: string;
  productId?: string | null;
  orderId?: string;
  legacyFallback?: ReactNode;
}) {
  const query = useMasterProductEvidence(patientId);
  if (!patientId) return legacyFallback ?? null;
  if (query.isLoading)
    return (
      <p className="type-body-sm text-ink-muted p-11">
        Loading intake evidence…
      </p>
    );
  if (query.error)
    return (
      <div role="alert" className="p-11 border border-danger/30 type-body-sm">
        Intake evidence could not be loaded.{" "}
        <button
          type="button"
          className="underline"
          onClick={() => {
            void query.refetch();
          }}
        >
          Retry
        </button>
      </div>
    );
  const evidence = query.data;
  if (!evidence) return legacyFallback ?? null;
  const { requirements, submissions } = selectIntakeEvidence(
    evidence,
    productId,
    orderId,
  );
  if (!submissions.length && !requirements.length)
    return legacyFallback ?? null;
  const highlights = combinedHighlights(submissions);
  const flags = Array.from(
    new Map(
      submissions
        .flatMap((item) => item.flags ?? [])
        .map((flag) => [`${flag.code}:${flag.message}`, flag]),
    ).values(),
  );
  const missingMaster = submissions.some(
    (item) =>
      item.master_submission_id &&
      !submissions.some((master) => master.id === item.master_submission_id),
  );
  return (
    <div
      className="ph-no-capture flex flex-col gap-14"
      aria-label="Annual and product intake evidence"
    >
      <section
        className="border border-line-subtle"
        aria-label="Clinical highlights"
      >
        <div className="p-11 bg-sunken border-b border-line-subtle">
          <h3 className="type-label-sm font-medium">Clinical highlights</h3>
          <p className="text-11 text-ink-muted mt-3">
            Annual history and the product intake for this assessment.
          </p>
        </div>
        {(flags.length > 0 || missingMaster) && (
          <div className="p-11 border-b border-danger/30 bg-danger-wash">
            {flags.map((flag) => (
              <p
                key={`${flag.code}:${flag.message}`}
                className="type-body-sm text-danger"
              >
                {flag.message}
              </p>
            ))}
            {missingMaster && (
              <p role="alert" className="type-body-sm text-danger">
                The referenced annual intake revision is missing from this view.
                Resolve the evidence before review.
              </p>
            )}
          </div>
        )}
        {highlights.length ? (
          <div className="p-11">
            <IntakeHighlights entries={highlights} />
          </div>
        ) : (
          <p className="p-11 type-body-sm text-ink-muted">
            No clinical highlights are recorded. Expand the intakes below to
            review the full answers.
          </p>
        )}
      </section>
      <section
        className="border border-line-subtle"
        aria-label="Full intake answers"
      >
        <div className="p-11 bg-sunken border-b border-line-subtle flex items-center gap-6">
          <FileText size={14} aria-hidden />
          <div>
            <h3 className="type-label-sm font-medium">Full intake answers</h3>
            <p className="text-11 text-ink-muted">
              Expand the annual or product intake to review every answer.
            </p>
          </div>
        </div>
        {submissions.map((submission) => (
          <IntakeEvidenceDetails
            key={submission.id}
            submission={submission}
            submissions={submissions}
          />
        ))}
        {!submissions.length &&
          (requirements.some(
            (item) =>
              item.status === "master_product_intake_evidence_unavailable",
          ) ? (
            <p role="alert" className="p-11 type-body-sm text-danger">
              The intake evidence for this assessment is unavailable. Resolve
              the evidence before review.
            </p>
          ) : (
            <p className="p-11 type-body-sm text-warning">
              The required intake has not been submitted yet.
            </p>
          ))}
      </section>
    </div>
  );
}
